import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { membersAPI } from '../../services/api';
import { LoadingPage } from '../../components/Loading';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import Pagination from '../../components/Pagination';
import MemberQRScanner from './MemberQRScanner';
import { CATEGORY_LABELS, membershipStatus } from '../../utils/membership';
import './Admin.css';

const Members = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [expiryFilter, setExpiryFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [highlightedMemberId, setHighlightedMemberId] = useState(null);
  const { success, error } = useToast();
  const { user } = useAuth();
  const hideContact = user?.desk_role === 'volunteer';
  const admin = user?.desk_role === 'admin';
  const { t, formatDate } = useLanguage();
  const navigate = useNavigate();

  const exportToCSV = () => {
    try {
      // CSV header
      const headers = ['會員編號', '中文姓名', '英文姓名', '電話', 'Email', '性別', '出生日期', '地區', '加入日期', '狀態'];
      
      // CSV rows
      const rows = filteredMembers.map(member => [
        member.member_number || '',
        member.chinese_name || '',
        member.english_name || '',
        member.user?.phone_number || member.phone_number || '',
        member.email || '',
        member.gender || '',
        member.birth_date || '',
        member.district || '',
        formatDate(member.created_at),
        member.is_active ? '啟用中' : '已停用'
      ]);
      
      // Combine headers and rows
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');
      
      // Create blob and download
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `會員列表_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      success('會員列表已匯出');
    } catch (err) {
      error('匯出失敗');
      console.error('Export error:', err);
    }
  };

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const response = await membersAPI.getAll();
      setMembers(Array.isArray(response.data) ? response.data : response.data?.results || []);
    } catch (err) {
      error('無法載入會員列表');
      console.error('Error fetching members:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async (memberId) => {
    if (!window.confirm('確定要啟用此會員？')) return;
    
    try {
      await membersAPI.patch(memberId, { is_active: true });
      success('會員已啟用');
      fetchMembers();
    } catch (err) {
      error('啟用失敗');
      console.error('Error activating member:', err);
    }
  };

  const handleDeactivate = async (memberId) => {
    if (!window.confirm('確定要停用此會員？')) return;
    
    try {
      await membersAPI.patch(memberId, { is_active: false });
      success('會員已停用');
      fetchMembers();
    } catch (err) {
      error('停用失敗');
      console.error('Error deactivating member:', err);
    }
  };

  const filteredMembers = members.filter(member => {
    const matchesSearch = 
      member.chinese_name?.toLowerCase().includes(search.toLowerCase()) ||
      member.english_name?.toLowerCase().includes(search.toLowerCase()) ||
      member.user?.phone_number?.includes(search) ||
      member.phone_number?.includes(search) ||
      member.member_number?.includes(search);

    const matchesStatus = 
      statusFilter === 'all' ||
      (statusFilter === 'active' && member.is_active) ||
      (statusFilter === 'inactive' && !member.is_active);

    const status = membershipStatus(member.membership_expires_on);
    const matchesExpiry = expiryFilter === 'all' || status.key === expiryFilter;
    const matchesCategory = categoryFilter === 'all' || member.member_category === categoryFilter;

    return matchesSearch && matchesStatus && matchesExpiry && matchesCategory;
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredMembers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedMembers = filteredMembers.slice(startIndex, endIndex);

  const handleMemberFound = (member) => {
    setHighlightedMemberId(member.id);
    success(`已找到會員：${member.chinese_name}`);
    navigate(`/admin/members/${member.id}`);
  };

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter, categoryFilter, expiryFilter]);

  if (loading) return <LoadingPage message="載入會員資料中..." />;

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1>會員管理</h1>
        <div className="admin-actions">
          <input
            type="text"
            placeholder="搜尋會員姓名、電話、會員編號..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="search-input"
          />
          {!hideContact && (
            <Link to="/admin/members/new" className="btn btn-primary">新增會員</Link>
          )}
          {!hideContact && (
            <Link to="/admin/mailing" className="btn btn-secondary">通訊錄</Link>
          )}
          {!hideContact && admin && (
            <button onClick={async () => {
              const response = await membersAPI.exportXlsx();
              const url = URL.createObjectURL(response.data);
              const link = document.createElement('a');
              link.href = url;
              link.download = 'members.xlsx';
              link.click();
              success(t('members.exported'));
            }} className="btn btn-primary">
              {t('members.export')}
            </button>
          )}
        </div>
      </div>

      <MemberQRScanner onMemberFound={handleMemberFound} />

      <div className="filter-tabs">
        <button 
          className={statusFilter === 'all' ? 'active' : ''}
          onClick={() => setStatusFilter('all')}
        >
          全部 ({members.length})
        </button>
        <button 
          className={statusFilter === 'active' ? 'active' : ''}
          onClick={() => setStatusFilter('active')}
        >
          啟用中 ({members.filter(m => m.is_active).length})
        </button>
        <button 
          className={statusFilter === 'inactive' ? 'active' : ''}
          onClick={() => setStatusFilter('inactive')}
        >
          已停用 ({members.filter(m => !m.is_active).length})
        </button>
        <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} aria-label="會員類別">
          <option value="all">全部類別</option>
          {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
        <select value={expiryFilter} onChange={(e) => setExpiryFilter(e.target.value)} aria-label="會籍狀態">
          <option value="all">全部會籍</option>
          <option value="valid">有效</option>
          <option value="soon">即將到期</option>
          <option value="expired">已過期</option>
          <option value="none">無到期</option>
        </select>
      </div>

      <div className="admin-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>會員編號</th>
              <th>姓名</th>
              {!hideContact && <th>電話</th>}
              {!hideContact && <th>Email</th>}
              <th>加入日期</th>
              <th>類別</th>
              <th>會籍</th>
              <th>狀態</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {paginatedMembers.length === 0 ? (
              <tr>
                  <td colSpan={hideContact ? '7' : '9'} style={{ textAlign: 'center', padding: '2rem' }}>
                  沒有符合條件的會員
                </td>
              </tr>
            ) : (
              paginatedMembers.map(member => (
                <tr
                  key={member.id}
                  className={highlightedMemberId === member.id ? 'row-highlighted' : ''}
                >
                  <td>{member.member_number}</td>
                  <td>
                    <Link to={`/admin/members/${member.id}`}>{member.chinese_name}</Link>
                    {member.english_name && <div className="subtitle">{member.english_name}</div>}
                  </td>
                  {!hideContact && <td>{member.phone_number || member.user?.phone_number}</td>}
                  {!hideContact && <td>{member.email || '-'}</td>}
                  <td>{formatDate(member.created_at)}</td>
                  <td>{CATEGORY_LABELS[member.member_category] || '普通會員'}</td>
                  <td>
                    <span className={`membership-badge membership-${membershipStatus(member.membership_expires_on).key}`}>
                      {membershipStatus(member.membership_expires_on).label}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge ${member.is_active ? 'status-active' : 'status-inactive'}`}>
                      {member.is_active ? '啟用中' : '已停用'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      {member.is_active ? (
                        <button 
                          onClick={() => handleDeactivate(member.id)}
                          className="btn btn-sm btn-danger"
                        >
                          停用
                        </button>
                      ) : (
                        <button 
                          onClick={() => handleActivate(member.id)}
                          className="btn btn-sm btn-success"
                        >
                          啟用
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        itemsPerPage={itemsPerPage}
        totalItems={filteredMembers.length}
      />
    </div>
  );
};

export default Members;
